/** @jsx React.createElement */
import React, { useState, useEffect } from 'react';
import { apiGet, apiPut, isAuthenticated } from '../utils/api';

const ThemeContext = React.createContext();

export const useTheme = () => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [loading, setLoading] = useState(true);

  // Charger le thème depuis l'API au démarrage
  useEffect(() => {
    loadTheme();
  }, []);

  // Appliquer le thème au DOM
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const loadTheme = async () => {
    try {
      // Vérifier si l'utilisateur est authentifié avant de charger le thème
      if (!isAuthenticated()) {
        // Utilisateur non authentifié, utiliser le thème par défaut
        setTheme('light');
        setLoading(false);
        return;
      }
      
      const response = await apiGet('/api/entreprise/appearance', { silent: true });
      if (response.success && response.settings) {
        const savedTheme = response.settings.theme || 'light';
        setTheme(savedTheme);
      } else {
        setTheme('light');
      }
    } catch (error) {
      // En cas d'erreur (token manquant, etc.), utiliser le thème par défaut
      // Ne pas afficher d'erreur car cela peut se produire avant l'authentification
      setTheme('light');
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = (newTheme) => {
    console.log('Application du thème:', newTheme);
    
    // Supprimer les classes de thème existantes
    document.documentElement.classList.remove('light-theme', 'dark-theme');
    document.body.classList.remove('light-theme', 'dark-theme');
    
    // Ajouter la nouvelle classe de thème
    document.documentElement.classList.add(`${newTheme}-theme`);
    document.body.classList.add(`${newTheme}-theme`);
    
    // Définir l'attribut data-theme
    document.documentElement.setAttribute('data-theme', newTheme);
    
  };

  const toggleTheme = async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    console.log('Changement de thème:', theme, '->', newTheme);
    
    setTheme(newTheme);
    
    // Sauvegarder le thème en base de données
    try {
      await apiPut('/api/entreprise/appearance', { theme: newTheme });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du thème:', error);
    }
  };

  const setThemeValue = async (newTheme) => {
    console.log('Définition du thème:', newTheme);
    
    setTheme(newTheme);
    
    // Sauvegarder le thème en base de données
    try {
      await apiPut('/api/entreprise/appearance', { theme: newTheme });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du thème:', error);
    }
  };

  const value = {
    theme,
    loading,
    toggleTheme,
    setTheme: setThemeValue,
    isLight: theme === 'light',
    isDark: theme === 'dark'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;




